import { SupportedLocation, CategorizedEmployerGroup } from "../data/locations/types";
import { getNearbyQualifiedLocations } from "./locationRegistry";

export interface HiringActivitySnapshot {
  businessesIndexed: number;
  hiringSignalScore: number;
  estimatedUnpostedOpportunitiesMin: number;
  estimatedUnpostedOpportunitiesMax: number;
  recentlyUpdatedCount: number;
  topHiringSectors: { title: string; growthIndicator: string }[];
  averageResponseWindow: string;
}

export interface NearbyLocationContext {
  citySlug: string;
  cityName: string;
  stateAbbreviation: string;
  stateName: string;
  distanceMilesEstimate: number;
  strategicReason: string;
  jobsUrl: string;
  hiringUrl: string;
  internshipsUrl: string;
}

export interface LocationFaqItem {
  question: string;
  answer: string;
}

export interface ContextualGuide {
  title: string;
  slug: string;
  path: string;
  description: string;
  category: string;
}

/**
 * Dynamic Location Content Engine for BizNearYou Location Pages
 */

/**
 * 1. INTRODUCTION GENERATOR
 * Generates natural, comprehensive, location-specific introductory text.
 */
export function getDynamicIntroduction(
  loc: SupportedLocation,
  variant: "jobs" | "businesses-hiring" | "internships"
): {
  headlineSummary: string;
  detailedParagraphs: string[];
  keyHighlightsList: string[];
} {
  const { cityName, stateName, stateAbbreviation, countryName, businessCount, priority, localHighlights } = loc;
  const metro = localHighlights.metroName.split("(")[0].trim();
  const topSectorTitles = localHighlights.topSectors.map(s => s.title).join(", ");
  const districtNames = localHighlights.majorDistricts.map(d => d.name).join(", ");

  // Estimate population range if not explicitly provided
  const popText = localHighlights.populationEstimate || 
    (priority === 1 ? "over 500,000 residents" : priority === 2 ? "between 100,000 and 500,000 residents" : "a vibrant regional population");

  let headlineSummary = "";
  if (variant === "jobs") {
    headlineSummary = `Explore ${businessCount}+ verified local employers, commercial practices, and direct hiring contacts across ${cityName}, ${stateName}.`;
  } else if (variant === "businesses-hiring") {
    headlineSummary = `Track active hiring signals, review momentum, and commercial expansion indicators across ${cityName}, ${stateAbbreviation}.`;
  } else {
    headlineSummary = `Discover unlisted internship, apprenticeship, and early-career experience opportunities in ${cityName}, ${stateName}.`;
  }

  const p1 = `${cityName} serves as a key commercial hub in the ${metro} across ${stateName}, ${countryName}, supporting ${popText}. The local economy is driven by a resilient mix of industries including ${topSectorTitles}. Unlike corporate job portals that force applicants through automated keyword filters, ${cityName}'s business community includes hundreds of independent practices, engineering vendors, specialized clinics, and mid-sized employers where key decision-makers manage hiring directly.`;

  let p2 = "";
  if (variant === "jobs") {
    p2 = `BizNearYou indexes ${businessCount}+ verified business locations across ${cityName}'s primary commercial corridors—including ${districtNames}. By monitoring digital footprints, customer review growth, and operational updates, job seekers can identify growing firms before formal employment ads are published on national job boards.`;
  } else if (variant === "businesses-hiring") {
    p2 = `Commercial growth in ${cityName} often translates into staffing needs weeks before a formal position is posted online. Our hiring signal engine analyzes real-time public indicators—such as website expansions, new location openings, and client review velocity—helping candidates reach out at the precise moment local firms need additional capacity.`;
  } else {
    p2 = `For students and early-career professionals in ${cityName}, private firms, law offices, medical centers, and trade offices represent the most receptive environment for custom internships. Direct outreach allows candidates to pitch customized support roles directly to managing partners and business owners.`;
  }

  const p3 = `Navigating ${cityName}'s unadvertised job market requires targeting mid-sized employers (10–50 staff) across districts like ${districtNames}. Approaching local owners directly with a tailored pitch yields a significantly higher response rate than submitting generic resumes into high-volume corporate portals.`;

  const keyHighlightsList = [
    `${businessCount}+ Indexed Local Business Locations in ${cityName}`,
    `Dominant Sectors: ${topSectorTitles}`,
    `Primary Hubs: ${districtNames}`,
    `Verified Coverage Confidence Score: ${loc.dataCoverageScore}%`
  ];

  return { headlineSummary, detailedParagraphs: [p1, p2, p3], keyHighlightsList };
}

/**
 * 2. CATEGORIZED EMPLOYERS GENERATOR
 * Organizes anchor institutions and local firms into clear industry verticals.
 */
export function getCategorizedEmployers(loc: SupportedLocation): CategorizedEmployerGroup[] {
  if (loc.localHighlights.categorizedEmployers && loc.localHighlights.categorizedEmployers.length > 0) {
    return loc.localHighlights.categorizedEmployers;
  }

  const { cityName, localHighlights } = loc;
  const institutions = localHighlights.majorInstitutions || [];
  const sectors = localHighlights.topSectors;

  const categories: CategorizedEmployerGroup[] = [
    {
      category: "Healthcare & Medical Sciences",
      iconName: "Activity",
      description: `Outpatient clinics, regional hospital networks, dental practices, and physical therapy centers in ${cityName}.`,
      employers: institutions.filter(i => /health|hospital|medical|clinic|care|pharmacy/i.test(i))
    },
    {
      category: "Higher Education & Research",
      iconName: "GraduationCap",
      description: `Universities, community colleges, vocational institutes, and educational centers.`,
      employers: institutions.filter(i => /university|college|school|institute|academy/i.test(i))
    },
    {
      category: "Government, Legal & Civil Services",
      iconName: "Building2",
      description: `Municipal offices, county departments, administrative law firms, and public agencies.`,
      employers: institutions.filter(i => /gov|city|county|state|court|board|authority/i.test(i))
    },
    {
      category: "Engineering, Manufacturing & Trade Contractors",
      iconName: "Wrench",
      description: `Precision machining shops, HVAC suppliers, electrical contractors, and commercial builders in ${cityName}.`,
      employers: institutions.filter(i => /machin|build|construct|eng|mfg|manufactur|corp|inc/i.test(i))
    },
    {
      category: "Professional Services, Finance & IT",
      iconName: "Briefcase",
      description: `Accounting practices, marketing agencies, regional banks, and managed IT service providers.`,
      employers: []
    }
  ];

  // Distribute remaining institutions that didn't match specific regex keywords
  const matched = new Set(categories.flatMap(c => c.employers));
  const unmatched = institutions.filter(i => !matched.has(i));

  if (unmatched.length > 0) {
    categories[4].employers.push(...unmatched);
  }

  // Ensure default fallback items if specific employer names are sparse
  if (categories[0].employers.length === 0) {
    categories[0].employers.push(`${cityName} Regional Health Center`, `${cityName} Private Specialty Clinics`);
  }
  if (categories[3].employers.length === 0) {
    categories[3].employers.push(`${cityName} Commercial Construction Partners`, `${cityName} Industrial Machining & Logistics`);
  }
  if (categories[4].employers.length === 0) {
    categories[4].employers.push(`${cityName} Commercial Law Practices`, `${cityName} CPA & Advisory Firms`);
  }

  // Filter out empty groups
  return categories.filter(c => c.employers.length > 0);
}

/**
 * 3. HIRING ACTIVITY SNAPSHOT GENERATOR
 */
export function getHiringActivitySnapshot(loc: SupportedLocation): HiringActivitySnapshot {
  const { businessCount, dataCoverageScore, localHighlights } = loc;

  const minUnposted = Math.round(businessCount * 0.35);
  const maxUnposted = Math.round(businessCount * 0.72);
  const recentlyUpdated = Math.round(businessCount * 0.44);

  const topHiringSectors = localHighlights.topSectors.map((s, idx) => ({
    title: s.title,
    growthIndicator: idx === 0 ? "High Inbound Demand" : idx === 1 ? "Expanding Staffing" : "Steady Hiring Signals"
  }));

  return {
    businessesIndexed: businessCount,
    hiringSignalScore: dataCoverageScore,
    estimatedUnpostedOpportunitiesMin: minUnposted,
    estimatedUnpostedOpportunitiesMax: maxUnposted,
    recentlyUpdatedCount: recentlyUpdated,
    topHiringSectors,
    averageResponseWindow: "24–48 Hours for Direct Owner Pitches"
  };
}

/**
 * 4. OUTREACH STRATEGY GENERATOR
 */
export function getOutreachStrategy(
  loc: SupportedLocation,
  variant: "jobs" | "businesses-hiring" | "internships"
): {
  targetProfile: string;
  whyItWorks: string;
  bestTiming: string;
  preferredChannels: string[];
  receptiveSectors: string[];
  networkingVenues: string[];
  customScript: string;
} {
  const { cityName, stateAbbreviation } = loc;

  const targetProfile = `Independent commercial businesses in ${cityName} with 10–50 employees. Focus on managing partners, clinic directors, operations heads, and owner-operators.`;

  const whyItWorks = `Small-to-midsize business owners in ${cityName} rarely have dedicated HR departments. They review inbound emails personally and welcome proactive, skilled candidates who offer direct operational support without advertising costs.`;

  const bestTiming = `Tuesdays through Thursdays between 8:30 AM and 10:00 AM local time. Avoid Monday mornings (busy with planning) and Friday afternoons.`;

  const preferredChannels = [
    `Direct Email to Owner/Manager (Primary)`,
    `In-Person Casual Drop-in for Local Retail/Trade (Secondary)`,
    `LinkedIn Message to Managing Partner (Tertiary)`
  ];

  const receptiveSectors = loc.localHighlights.topSectors.map(s => s.title);

  const networkingVenues = [
    `${cityName} Area Chamber of Commerce`,
    `${cityName} Regional Business Network International (BNI) Chapter`,
    `${cityName} Young Professionals & Industry Meetups`
  ];

  let customScript = "";
  if (variant === "jobs") {
    customScript = `Subject: Proactive Inquiry – [Your Field / Specialty] for [Company Name] in ${cityName}

Dear [Owner / Hiring Manager Name],

I am reaching out directly because I've been closely following [Company Name]'s growth and reputational footprint here in ${cityName}.

I am a skilled [Your Field / Role, e.g., Medical Assistant / CAD Designer / Operations Specialist] based right here in ${cityName}, ${stateAbbreviation}. I specialize in [Key Skill 1, e.g., workflow optimization, patient triage, client communications], and I admire your recent work in [Specific Service / Industry].

I understand that local businesses don't always post open roles publicly. I would welcome a brief 10-minute phone call or coffee to introduce myself, share my resume, and learn if your team could use extra support this quarter.

Thank you for your time and dedication to the ${cityName} community.

Best regards,

[Your Full Name]
[Your Phone Number] | [Your Email]
[Link to Portfolio / Resume]`;
  } else if (variant === "businesses-hiring") {
    customScript = `Subject: Supporting [Company Name]'s Expansion in ${cityName}

Dear [Owner / Decision Maker Name],

Congratulations on [Company Name]'s continued momentum in ${cityName}. I noticed your team's expanding client base and strong local presence.

As an experienced [Your Role / Specialty] based in ${cityName}, I help growing local firms handle increased demand without operational bottlenecks. My background includes [1-2 sentence summary of key achievement or technical skill].

If you are evaluating team expansion or need reliable capacity as your client volume grows, I would love 5 minutes to discuss how I can add immediate value.

Best regards,

[Your Name]
[Phone Number] | [Email Address]
[Portfolio / LinkedIn]`;
  } else {
    customScript = `Subject: Student Intern / Assistant Inquiry – [Company Name] (${cityName})

Dear [Owner / Department Head Name],

My name is [Your Name], and I am currently pursuing studies in [Your Major / Field] in the ${cityName} area.

I am reaching out directly to [Company Name] because of your respected work in [Industry / Niche]. I am seeking a hands-on internship or assistant role where I can support your daily operations in exchange for practical experience.

I am proficient in [Software / Tool 1], [Skill 2], and eager to assist with [Administrative / Technical / Operational tasks].

Would you be open to a 10-minute conversation to explore if a flexible intern placement could benefit your team this upcoming term?

Sincerely,

[Your Name]
[Phone Number] | [Email Address]
[School / Program Name]`;
  }

  return {
    targetProfile,
    whyItWorks,
    bestTiming,
    preferredChannels,
    receptiveSectors,
    networkingVenues,
    customScript
  };
}

/**
 * 5. DYNAMIC CITY-SPECIFIC LOCAL FAQ GENERATOR
 */
export function getDynamicFaqs(
  loc: SupportedLocation,
  variant: "jobs" | "businesses-hiring" | "internships"
): LocationFaqItem[] {
  const { cityName, stateName, stateAbbreviation, businessCount, localHighlights } = loc;
  const metro = localHighlights.metroName.split("(")[0].trim();
  const districtNames = localHighlights.majorDistricts.map(d => d.name).slice(0, 2).join(" and ");
  const topSector = localHighlights.topSectors[0]?.title || "Local Commercial Services";

  return [
    {
      question: `Are businesses in ${cityName}, ${stateAbbreviation} actively hiring right now?`,
      answer: `Yes. Beyond formal job postings on national boards, much of local hiring across ${cityName}'s ${businessCount}+ commercial employers occurs through direct outreach, word-of-mouth, and unadvertised openings. Local practices in healthcare, legal services, trade contracting, and technical services regularly add staff when qualified candidates reach out directly.`
    },
    {
      question: `Which industries are growing fastest in ${cityName}?`,
      answer: `The fastest-growing commercial sectors in ${cityName} include ${topSector}, alongside regional health networks, technical trade contractors, and commercial advisory firms across key corridors like ${districtNames}.`
    },
    {
      question: `How can I find unlisted jobs and internships in ${cityName}?`,
      answer: `To unlock unlisted opportunities in ${cityName}, use BizNearYou to scout independent businesses with 10–50 staff in districts like ${districtNames}. Identify managing partners or owners and send a concise direct email expressing interest in their firm.`
    },
    {
      question: `Where are the highest concentrations of employers located in ${cityName}?`,
      answer: `Employer density in ${cityName} is highest across major commercial zones including ${districtNames}, as well as office parks throughout the ${metro} corridor.`
    },
    {
      question: `Why is direct outreach to ${cityName} owners better than applying on job boards?`,
      answer: `Applying on major job boards places your resume alongside hundreds of applicant submissions evaluated by automated ATS filters. Direct outreach to local ${cityName} business owners bypasses algorithms, connects you straight to decision-makers, and demonstrates proactive initiative.`
    },
    {
      question: `How does BizNearYou verify business data in ${cityName}?`,
      answer: `BizNearYou continuously cross-references public business filings, commercial registry updates, web footprint indicators, and customer review velocity. ${cityName} holds a ${loc.dataCoverageScore}% verified data confidence rating.`
    }
  ];
}

/**
 * 6. STRATEGIC NEARBY LOCATIONS GENERATOR WITH CONTEXT
 */
export function getNearbyLocationsWithContext(
  currentLoc: SupportedLocation,
  limit: number = 6
): NearbyLocationContext[] {
  const nearbyQualified = getNearbyQualifiedLocations(currentLoc.citySlug, limit);

  return nearbyQualified.map((nLoc, idx) => {
    const distanceEst = Math.round(10 + (idx + 1) * 6.5);
    const topSector = nLoc.localHighlights.topSectors[0]?.title || "Commercial Hub";

    let strategicReason = "";
    if (/health|medical/i.test(topSector)) {
      strategicReason = `Major regional healthcare and specialized medical practice hub (${distanceEst} miles away).`;
    } else if (/tech|software|engineering/i.test(topSector)) {
      strategicReason = `High concentration of technology, CAD engineering, and IT suppliers (${distanceEst} miles away).`;
    } else if (/trade|mfg|manufactur|construct/i.test(topSector)) {
      strategicReason = `Strong industrial manufacturing, logistics, and trade contracting corridor (${distanceEst} miles away).`;
    } else {
      strategicReason = `Expanded regional commercial market with ${nLoc.businessCount}+ indexed businesses (${distanceEst} miles away).`;
    }

    return {
      citySlug: nLoc.citySlug,
      cityName: nLoc.cityName,
      stateAbbreviation: nLoc.stateAbbreviation,
      stateName: nLoc.stateName,
      distanceMilesEstimate: distanceEst,
      strategicReason,
      jobsUrl: `/jobs/${nLoc.citySlug}`,
      hiringUrl: `/businesses-hiring/${nLoc.citySlug}`,
      internshipsUrl: `/internships/${nLoc.citySlug}`
    };
  });
}

/**
 * 7. CONTEXTUAL GUIDES GENERATOR
 */
export function getContextualGuides(
  loc: SupportedLocation,
  variant: "jobs" | "businesses-hiring" | "internships"
): ContextualGuide[] {
  const { cityName } = loc;

  return [
    {
      title: `How to Email a Business Owner About a Job in ${cityName}`,
      slug: "how-to-email-a-business-about-a-job",
      path: "/guides/how-to-email-a-business-about-a-job",
      description: `Step-by-step email templates and timing strategies to pitch owners of mid-sized firms in ${cityName} without an open job ad.`,
      category: "Cold Outreach"
    },
    {
      title: `How to Ask if a Local ${cityName} Business is Hiring`,
      slug: "how-to-ask-if-a-business-is-hiring",
      path: "/guides/how-to-ask-if-a-business-is-hiring",
      description: `In-person and phone scripts for approaching managing partners and office managers across ${cityName} commercial districts.`,
      category: "Direct Inquiries"
    },
    {
      title: `Unlocking Hidden Jobs in Small & Mid-Sized Metros`,
      slug: "how-to-find-hidden-jobs",
      path: "/guides/how-to-find-hidden-jobs",
      description: `Discover how local positions in ${cityName} are filled through informal networking and direct outreach before reaching job boards.`,
      category: "Hidden Job Market"
    },
    {
      title: `Landing Entry-Level Roles in ${cityName} Without Prior Experience`,
      slug: "how-to-find-a-job-with-no-experience",
      path: "/guides/how-to-find-a-job-with-no-experience",
      description: `Position your transferable skills, course projects, and motivation to win over small business owners in ${cityName}.`,
      category: "Career Starters"
    }
  ];
}

/**
 * 8. COMPREHENSIVE JSON-LD STRUCTURED DATA GENERATOR
 */
export function getLocationJsonLdSchema(
  loc: SupportedLocation,
  variant: "jobs" | "businesses-hiring" | "internships",
  canonicalUrl: string,
  faqs: LocationFaqItem[],
  breadcrumbItems: { label: string; path: string }[]
): object[] {
  const { cityName, stateName, stateAbbreviation, countryName, lat, lng, businessCount, localHighlights } = loc;

  // Place / City schema
  const placeSchema = {
    "@context": "https://schema.org",
    "@type": "City",
    "name": cityName,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": cityName,
      "addressRegion": stateAbbreviation,
      "addressCountry": countryName
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": lat,
      "longitude": lng
    }
  };

  // WebPage / CollectionPage schema
  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${cityName}, ${stateAbbreviation} Local Business & Employment Guide`,
    "description": `Discover ${businessCount}+ local businesses, hiring signals, and direct outreach leads in ${cityName}, ${stateName}.`,
    "url": canonicalUrl,
    "about": placeSchema,
    "publisher": {
      "@type": "Organization",
      "name": "BizNearYou",
      "url": "https://getbiznearyou.com",
      "logo": "https://getbiznearyou.com/icon.png"
    }
  };

  // FAQPage schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return [pageSchema, faqSchema];
}
